import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StatsLeadersTable as Table } from './components/StatsLeadersTable';
import { StatsSection } from '../../../common/stats-section';
import { Loader } from '../../../../../../../../common/react/components/Loader';
import {
    playerStatsSelector,
    statsTypeSelector,
    isReadyToFetchSelector,
    roundSelector,
    seasonSelector,
    statsLeadersNavSelector,
    statsLeadersBenchmarkingSelector,
    statsLeadersRequestsSelector,
    statsLeadersEmptyRequestsSelector,
    statsLeadersDataTypeSelector
} from '../../../../../../store/js/modules/statspro/stats-leaders/selectors';
import { FETCH_STATS_LEADERS } from '../../../../../../store/js/modules/statspro/stats-leaders/actions';
import { composeRowDataForPlayer, composeColumnData } from './utils';
import { sortPlayersBySurname } from '../../../common/utils/sort-players-by-surname';
import { getTranslation } from '../../../../../../../../common/react/utils/translations';

export const StatsLeadersTable = () => {
    const dispatch = useDispatch();

    const players = useSelector(playerStatsSelector);
    const nav = useSelector(statsLeadersNavSelector);
    const benchmarking = useSelector(statsLeadersBenchmarkingSelector);
    const dataType = useSelector(statsLeadersDataTypeSelector);
    const season = useSelector(seasonSelector);
    const round = useSelector(roundSelector);
    const statsType = useSelector(statsTypeSelector);
    const isReadyToFetch = useSelector(isReadyToFetchSelector);
    const requests = useSelector(statsLeadersRequestsSelector);
    const emptyRequests = useSelector(statsLeadersEmptyRequestsSelector);

    const [isEmpty, setIsEmpty] = useState(false);

    /**
     * 1. Check that we haven't tried a request already and it's come up empty
     *
     * 2. Check that a request for this information is not already in progress
     *    before proceeding.
     *
     * @param {object} arg - Object.
     * @param {'standard'|'benchmarking'} arg.type - Type of request.
     */
    const fetchStatsLeaders = ({ type }) => {
        const reqArgs = {
            id: (statsType === 'seasons' ? season : round).providerId,
            type: statsType,
            benchmarking: type === 'benchmarking'
        };

        // [1]
        const matchingEmptyRequest = emptyRequests.some((req) =>
            _.isEqual(req, reqArgs)
        );
        if (matchingEmptyRequest) {
            setIsEmpty(true);
            return;
        }

        // [2]
        const existingRequest = requests.find((req) => _.isEqual(req, reqArgs));
        if (existingRequest) {
            return;
        }

        dispatch(FETCH_STATS_LEADERS.request(reqArgs));
    };

    useEffect(() => {
        if (!isReadyToFetch) {
            return;
        }

        setIsEmpty(false);

        if (!benchmarking.isActive && !playersHaveStandardData(players)) {
            fetchStatsLeaders({ type: 'standard' });
        }

        if (benchmarking.isActive && !playersHaveBenchmarkingData(players)) {
            fetchStatsLeaders({ type: 'benchmarking' });
        }
    }, [players, isReadyToFetch, benchmarking]);

    /**
     * Get column details.
     */
    const columns = useMemo(
        () => composeColumnData(nav.category),
        [nav.category]
    );

    /**
     * Get row data.
     */
    const data = useMemo(() => {
        if (
            (!benchmarking.isActive && !playersHaveStandardData(players)) ||
            (benchmarking.isActive && !playersHaveBenchmarkingData(players))
        ) {
            return [];
        }

        const dataColumns = columns.filter((col) => {
            return !['rank', 'player', 'position', 'team'].includes(
                col.accessor
            );
        });

        return [
            ...players
                .map((player) =>
                    composeRowDataForPlayer(player, dataColumns, dataType.type)
                )
                .sort(sortPlayersBySurname)
        ];
    }, [columns, players]);

    if (isEmpty) {
        return (
            <div style={{ padding: '3.2rem 1.6rem' }}>
                <StatsSection.ContentError
                    message={getTranslation(
                        'label.statspro.statsleaders.emptyTable'
                    )}
                />
            </div>
        );
    }

    if (!data.length) {
        return (
            <div style={{ padding: '3.2rem 1.6rem' }}>
                <Loader />
            </div>
        );
    }

    return (
        <Table
            columns={columns}
            data={data}
            players={players}
            modifier="stats-leaders"
        />
    );
};

const playersHaveStandardData = (players) =>
    players && players.length && players[0]?.stats?.totals;

const playersHaveBenchmarkingData = (players) =>
    players && players.length && players[0]?.stats?.benchmarkedTotals;
